import { Box, Button, LinearProgress, Paper, Typography } from '@mui/material'
import { FC } from 'react'
import { useScreenSize } from '../../../../hooks/useScreenSize'
import { Reward } from './Reward'

export interface ChallengeProps {
    title: string
    achievement: {
        id: number
    }
    threshold: number
    progress: number
    tooltip: string
    description: string
    assetUrl: string
    endDate: string // ISO 8601 date string
}

const normalise = (value, threshold) => {
    const normalisedValue = (value * 100) / threshold

    return Math.min(100, normalisedValue)
}

export const ChallengeCard: FC<ChallengeProps> = (challenge) => {
    const {
        title,
        achievement: { id },
        threshold,
        progress,
        tooltip,
        description,
        assetUrl,
        endDate,
    } = challenge
    const { isMobile } = useScreenSize()

    const isClaimable = progress >= threshold
    const headerTypographyVariant = isMobile ? 'h6' : 'h5'

    return (
        <Paper
            variant="outlined"
            sx={{
                borderColor: progress ? (isClaimable ? 'secondary.main' : 'primary.main') : 'common.white',
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    width: '100%',
                    gap: 1,
                    gridTemplateRows: '1fr',
                    gridTemplateColumns: '1fr auto',
                    gridTemplateAreas: {
                        xs: `"title status icon" 
                            "progress progress button"`,
                        md: `"title status icon" 
                            "progress button icon"`,
                    },
                }}
            >
                <Box sx={{ gridArea: 'title' }}>
                    <Typography variant={headerTypographyVariant} textTransform="uppercase">
                        {title}
                    </Typography>
                    <Typography textTransform={isMobile ? 'none' : 'uppercase'} variant="body2">
                        {description}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        width: { sx: 'unset', md: '150px' },
                        gridArea: 'status',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: { xs: 'flex-end', md: progress ? 'space-between' : 'flex-end' },
                        'p, h5, h6': {
                            textAlign: { xs: 'end', md: 'center' },
                        },
                    }}
                >
                    {!isMobile && Boolean(progress) && (
                        <Typography
                            textTransform="uppercase"
                            variant={headerTypographyVariant}
                            color={isClaimable ? 'secondary' : 'primary'}
                        >
                            {isClaimable ? 'Completed' : 'In Progress'}
                        </Typography>
                    )}
                    <Typography fontWeight={isMobile ? 'none' : 'bold'} variant="body2">
                        7D 6H 2M
                    </Typography>
                </Box>
                <Box sx={{ gridArea: 'progress' }}>
                    <LinearProgress
                        size={isMobile ? 'small' : 'medium'}
                        variant="determinate"
                        color={isClaimable ? 'secondary' : 'primary'}
                        value={normalise(progress, threshold)}
                        meter
                    />
                </Box>

                <Reward challenge={challenge} isClaimable={isClaimable} />
                {isClaimable ? (
                    <Button
                        size={isMobile ? 'small' : 'medium'}
                        variant="contained"
                        color="secondary"
                        fullWidth
                        sx={{ gridArea: 'button', minWidth: 'unset', padding: 0 }}
                    >
                        Claim
                    </Button>
                ) : (
                    <Typography
                        variant="body2"
                        sx={{
                            gridArea: 'button',
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: { xs: '18px', md: '24px' },
                            fontWeight: 'bold',
                        }}
                    >
                        {progress}/{threshold}
                    </Typography>
                )}
            </Box>
        </Paper>
    )
}
